import { useSensor, useSensors, PointerSensor, DragEndEvent } from '@dnd-kit/core'
import { TppMatrixState, DRAG_TYPES } from '../types'
import { useTppMutations } from './useTppMutations'

export const useTppDnD = (
    state: TppMatrixState | null,
    mutations: ReturnType<typeof useTppMutations>,
    callbacks?: {
        onCellMoveStart?: (cellId: string) => void
        onCellMoveEnd?: () => void
        onSlotReorderStart?: (slotId: string) => void
        onSlotReorderEnd?: () => void
    },
    options?: {
        disabled?: boolean
    }
) => {
    const disabled = options?.disabled ?? false
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

    const detectSmartDropTarget = (activeType: string, overData: any): string | null => {
        if (!state) return null
        if (!overData) return null
        if (overData.type === activeType) return null

        switch (activeType) {
            case DRAG_TYPES.COLUMN:
                if (overData.columnId) return overData.columnId
                break
            case DRAG_TYPES.SECTION:
                if (overData.sectionId) return overData.sectionId
                if (overData.rowId) return state.rows[overData.rowId]?.sectionId
                if (overData.slotId) {
                    const rowId = state.slots[overData.slotId]?.rowId
                    return state.rows[rowId]?.sectionId
                }
                break
            case DRAG_TYPES.ROW:
                if (overData.rowId) return overData.rowId
                if (overData.slotId) return state.slots[overData.slotId]?.rowId
                break
            case DRAG_TYPES.SLOT:
                if (overData.slotId) return overData.slotId
                break
        }
        return null
    }

    const handleDragStart = () => {
        if (!state || disabled) return
    }

    const handleDragEnd = (event: DragEndEvent) => {
        if (!state || disabled) return
        const { active, over } = event
        if (!over) return

        const activeType = active.data.current?.type
        const overData = over.data.current || {}

        if (activeType === DRAG_TYPES.COLUMN) {
            let targetId = over.id as string
            const smartId = detectSmartDropTarget(DRAG_TYPES.COLUMN, overData)
            if (smartId) targetId = smartId

            if (targetId && active.id !== targetId && state.tpp!.columnIds.includes(targetId)) {
                const oldIndex = state.tpp!.columnIds.indexOf(active.id as string)
                const newIndex = state.tpp!.columnIds.indexOf(targetId)
                const newOrder = [...state.tpp!.columnIds]
                newOrder.splice(oldIndex, 1)
                newOrder.splice(newIndex, 0, active.id as string)

                mutations.reorderColumns.mutate(newOrder)
            }
        }

        if (activeType === DRAG_TYPES.SECTION) {
            let targetId = over.id as string
            const smartId = detectSmartDropTarget(DRAG_TYPES.SECTION, overData)
            if (smartId) targetId = smartId

            if (targetId && active.id !== targetId && state.tpp!.sectionIds.includes(targetId)) {
                const oldIndex = state.tpp!.sectionIds.indexOf(active.id as string)
                const newIndex = state.tpp!.sectionIds.indexOf(targetId)
                const newOrder = [...state.tpp!.sectionIds]
                newOrder.splice(oldIndex, 1)
                newOrder.splice(newIndex, 0, active.id as string)

                mutations.reorderSections.mutate(newOrder)
            }
        }

        if (activeType === DRAG_TYPES.ROW) {
            const activeSectionId = active.data.current?.sectionId
            let targetRowId = over.id as string
            const smartId = detectSmartDropTarget(DRAG_TYPES.ROW, overData)
            if (smartId) targetRowId = smartId

            const targetRow = state.rows[targetRowId]
            if (targetRow && activeSectionId === targetRow.sectionId && active.id !== targetRowId) {
                const section = state.sections[activeSectionId]
                const oldIndex = section.rowIds.indexOf(active.id as string)
                const newIndex = section.rowIds.indexOf(targetRowId)
                const newOrder = [...section.rowIds]
                newOrder.splice(oldIndex, 1)
                newOrder.splice(newIndex, 0, active.id as string)

                mutations.reorderRows.mutate({ sectionId: activeSectionId, newOrder })
            }
        }

        if (activeType === DRAG_TYPES.SLOT) {
            const activeRowId = active.data.current?.rowId
            let targetSlotId = over.id as string
            const smartId = detectSmartDropTarget(DRAG_TYPES.SLOT, overData)
            if (smartId) targetSlotId = smartId

            const targetSlot = state.slots[targetSlotId]
            if (targetSlot && activeRowId === targetSlot.rowId && active.id !== targetSlotId) {
                const row = state.rows[activeRowId]
                const oldIndex = row.slotIds.indexOf(active.id as string)
                const newIndex = row.slotIds.indexOf(targetSlotId)
                const newOrder = [...row.slotIds]
                newOrder.splice(oldIndex, 1)
                newOrder.splice(newIndex, 0, active.id as string)

                callbacks?.onSlotReorderStart?.(active.id as string)
                mutations.reorderSlots.mutate(
                    { rowId: activeRowId, newOrder },
                    {
                        onSettled: () => callbacks?.onSlotReorderEnd?.(),
                    }
                )
            }
        }

        if (activeType === DRAG_TYPES.CELL) {
            const sourceSlotId = active.data.current?.slotId
            const sourceColumnId = active.data.current?.columnId
            let targetSlotId = overData.slotId
            let targetColumnId = overData.columnId

            if (sourceSlotId && sourceColumnId && targetSlotId && targetColumnId) {
                if (sourceSlotId === targetSlotId && sourceColumnId === targetColumnId) return
                const targetCell = state.cellLookup[targetSlotId]?.[targetColumnId]
                if (targetCell) return

                callbacks?.onCellMoveStart?.(active.id as string)
                mutations.moveCell.mutate(
                    {
                        cellId: active.id as string,
                        sourceSlotId,
                        sourceColumnId,
                        targetSlotId,
                        targetColumnId,
                    },
                    {
                        onSettled: () => callbacks?.onCellMoveEnd?.(),
                    }
                )
            }
        }
    }

    return { sensors: disabled ? [] : sensors, handleDragStart, handleDragEnd, disabled }
}
